import {
  SEVERITY_ORDER,
  SEVERITY_RANK,
  SEVERITY_SCORE,
  SEVERITY_COLORS,
  buildBrand,
  alpha,
  formatDate,
  extractSeverity,
  cleanReportDetails,
  markdownToBlocks,
  slugify,
} from '../pentest/reports/externalReportPdfHelpers';

export {
  SEVERITY_ORDER,
  SEVERITY_RANK,
  SEVERITY_SCORE,
  SEVERITY_COLORS,
  alpha,
};

export const DARKWEB_METHODOLOGY_PARAGRAPHS = [
  'We begin each dark web monitoring engagement by confirming the approved scope and identifying the domains, email addresses, and organizational identities that should be monitored. The assessment team validates the target set, normalizes those identifiers, and establishes the baseline that will be used to distinguish meaningful exposure from noise or unrelated third-party records.',
  'Once the scope is defined, we query curated leak sources and indexed datasets to identify records tied to the monitored organization. We collect exposed records, normalize fields across sources, and correlate repeated identities so we can understand whether a single account appears once or is repeatedly exposed across multiple leak repositories.',
  'After collection, we enrich the results with breach intelligence to determine which verified breach databases are associated with the exposed identities, when those breaches occurred, and what categories of data were disclosed. This allows the assessment team to evaluate both recency and likely impact while separating raw leak volume from validated breach events.',
  'We then review the resulting exposure patterns, document the identities and datasets most relevant to risk, and translate those results into reporting for both leadership and technical stakeholders. The goal is to provide a practical understanding of observed exposure, likely account risk, and the monitoring-driven actions that should be prioritized next.',
];

const safeJsonParse = (value, fallback) => {
  try {
    if (value == null) return fallback;
    if (typeof value === 'string') return JSON.parse(value);
    return value;
  } catch {
    return fallback;
  }
};

const normalizeEmail = (value = '') => String(value || '').trim().toLowerCase();

const uniq = (values = []) => Array.from(new Set(values));

const parseBreachDate = (breach) => {
  const raw = breach?.BreachDate || breach?.breach_date || breach?.date;
  if (!raw) return null;

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatShortDate = (value) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return 'N/A';

  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const stripHtmlKeepText = (value = '') =>
  String(value || '')
    .replace(/<a\b[^>]*>/gi, '')
    .replace(/<\/a>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();

const buildSummaryFallback = ({
  totalLeakRecords,
  uniqueEmailsCount,
  uniqueBreachDbsCount,
  mostRecentBreach,
}) => {
  const mostRecentText = mostRecentBreach?.date ? formatShortDate(mostRecentBreach.date) : 'N/A';

  return [
    `We identified ${totalLeakRecords} leaked record(s) tied to ${uniqueEmailsCount} unique monitored email address(es) during this monitoring review.`,
    `Breach enrichment mapped those identities to ${uniqueBreachDbsCount} unique breach database(s), with the most recent validated breach date observed on ${mostRecentText}.`,
    'The assessment team recommends prioritizing identity review, credential rotation where exposure is relevant, MFA enforcement, and ongoing monitoring for accounts that appear repeatedly across multiple breach sources.',
  ].join('\n\n');
};

const splitSummaryParagraphs = (value = '') =>
  String(value || '')
    .split(/\n{2,}/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

export const buildDarkwebReportPdfData = ({
  pentestId,
  primaryColor,
  companyName,
  clientName,
  reportResponse,
  leaksPayload,
}) => {
  const brand = buildBrand(primaryColor || '#0A68FF');

  const leakRows = Array.isArray(leaksPayload?.leaks) ? leaksPayload.leaks : [];
  const enrichmentRows = Array.isArray(leaksPayload?.hibp_breaches) ? leaksPayload.hibp_breaches : [];
  const reportDetailsRaw =
    reportResponse?.report_details ||
    reportResponse?.leaks?.report_details ||
    (Array.isArray(reportResponse?.leaks) ? reportResponse.leaks : []);

  const leakCountByEmail = new Map();
  const breachMapByEmail = new Map();
  const leakSourceCounts = new Map();
  const observedEmails = new Set();

  leakRows.forEach((row) => {
    const email = normalizeEmail(row?.email);
    if (email) {
      observedEmails.add(email);
      leakCountByEmail.set(email, (leakCountByEmail.get(email) || 0) + 1);
    }

    const source = String(row?.database_name || '').trim();
    if (source) {
      leakSourceCounts.set(source, (leakSourceCounts.get(source) || 0) + 1);
    }
  });

  enrichmentRows.forEach((row) => {
    const email = normalizeEmail(row?.email);
    if (!email) return;

    observedEmails.add(email);
    const breaches = safeJsonParse(row?.data, []);
    const breachList = Array.isArray(breaches) ? breaches : [];

    if (!breachMapByEmail.has(email)) {
      breachMapByEmail.set(email, new Map());
    }

    const byName = breachMapByEmail.get(email);

    breachList.forEach((breach) => {
      const name = String(breach?.Name || breach?.Title || breach?.name || '').trim();
      if (!name) return;

      const nextDate = parseBreachDate(breach);
      const current = byName.get(name);
      const currentDate = current ? parseBreachDate(current) : null;

      if (!current || (nextDate && (!currentDate || nextDate > currentDate))) {
        byName.set(name, breach);
      }
    });
  });

  const breachesByEmail = new Map(
    Array.from(breachMapByEmail.entries()).map(([email, breachMap]) => {
      const rows = Array.from(breachMap.values()).sort((left, right) => {
        const leftDate = parseBreachDate(left)?.getTime() || 0;
        const rightDate = parseBreachDate(right)?.getTime() || 0;
        return rightDate - leftDate;
      });

      return [email, rows];
    })
  );

  const findings = (Array.isArray(reportDetailsRaw) ? reportDetailsRaw : [])
    .filter((item) => item && (item.name || item.email || item.report_details))
    .map((item, index) => {
      const email = normalizeEmail(item?.name || item?.email || '');
      const title = item?.name || item?.email || `Identity ${index + 1}`;
      const severity = extractSeverity(item);
      const cleanedDetails = cleanReportDetails(item?.report_details || item?.details || '');
      const breaches = breachesByEmail.get(email) || [];
      const mostRecentBreach = breaches[0] || null;
      const mostRecentDate = parseBreachDate(mostRecentBreach);
      const mostRecentTimestamp = mostRecentDate?.getTime?.() || 0;

      observedEmails.add(email);

      return {
        ...item,
        id: `${slugify(title)}-${index + 1}`,
        title,
        email,
        severity,
        cleanedDetails,
        markdownBlocks: markdownToBlocks(cleanedDetails || 'No additional details provided.'),
        impactedRecordCount: leakCountByEmail.get(email) || 0,
        impactedBreachCount: breaches.length,
        mostRecentBreachText: mostRecentDate ? formatShortDate(mostRecentDate) : 'N/A',
        mostRecentBreachTs: mostRecentTimestamp,
        breachChips: breaches.map((breach) => {
          const name = String(breach?.Name || breach?.Title || breach?.name || 'Unknown breach').trim();
          const breachDate = parseBreachDate(breach);

          return {
            name,
            date: breachDate ? breachDate.toISOString() : null,
            isMostRecent: Boolean(mostRecentTimestamp && breachDate?.getTime?.() === mostRecentTimestamp),
          };
        }),
      };
    })
    .sort((left, right) => {
      const recencyDelta = (right.mostRecentBreachTs || 0) - (left.mostRecentBreachTs || 0);
      if (recencyDelta !== 0) return recencyDelta;

      const severityDelta = (SEVERITY_RANK[left.severity] ?? 999) - (SEVERITY_RANK[right.severity] ?? 999);
      if (severityDelta !== 0) return severityDelta;

      return String(left.title || '').localeCompare(String(right.title || ''));
    });

  const breachToEmails = new Map();
  const breachMeta = new Map();

  enrichmentRows.forEach((row) => {
    const email = normalizeEmail(row?.email);
    if (!email) return;

    const breaches = safeJsonParse(row?.data, []);
    const breachList = Array.isArray(breaches) ? breaches : [];

    breachList.forEach((breach) => {
      const name = String(breach?.Name || breach?.name || 'Unknown breach').trim();
      if (!name) return;

      if (!breachToEmails.has(name)) breachToEmails.set(name, new Set());
      breachToEmails.get(name).add(email);

      const breachDate = parseBreachDate(breach);
      const previous = breachMeta.get(name);
      const previousDate = previous?.date ? new Date(previous.date) : null;
      const nextClasses = Array.isArray(breach?.DataClasses)
        ? breach.DataClasses
        : Array.isArray(breach?.data_classes)
          ? breach.data_classes
          : [];

      breachMeta.set(name, {
        description:
          (breach?.Description && String(breach.Description).length > String(previous?.description || '').length)
            ? breach.Description
            : previous?.description || breach?.Description || '',
        date:
          breachDate && (!previousDate || breachDate > previousDate)
            ? breachDate.toISOString()
            : previous?.date || null,
        dataClasses: uniq([...(previous?.dataClasses || []), ...nextClasses]),
      });
    });
  });

  const breachRows = Array.from(breachToEmails.keys())
    .map((name) => {
      const meta = breachMeta.get(name) || {};
      return {
        name,
        description: stripHtmlKeepText(meta.description || ''),
        date: meta.date || null,
        dataClasses: meta.dataClasses || [],
        impactedEmails: breachToEmails.get(name)?.size || 0,
      };
    })
    .sort((left, right) => {
      const leftDate = left.date ? new Date(left.date).getTime() : 0;
      const rightDate = right.date ? new Date(right.date).getTime() : 0;
      if (rightDate !== leftDate) return rightDate - leftDate;
      return (right.impactedEmails || 0) - (left.impactedEmails || 0);
    });

  const totalLeakRecords = leakRows.length;
  const uniqueEmailsCount = Array.from(observedEmails).filter(Boolean).length;
  const uniqueBreachDbsCount = breachRows.length;
  const uniqueLeakSourceCount = leakSourceCounts.size;

  const mostRecentBreach = breachRows.find((item) => item.date) || null;

  const topImpactedEmails = Array.from(leakCountByEmail.entries())
    .map(([email, count]) => ({
      email,
      count,
      breachCount: breachesByEmail.get(email)?.length || 0,
    }))
    .sort((left, right) => {
      const countDelta = (right.count || 0) - (left.count || 0);
      if (countDelta !== 0) return countDelta;
      return (right.breachCount || 0) - (left.breachCount || 0);
    })
    .slice(0, 6);

  const topBreachDatabases = breachRows
    .map((item) => ({
      name: item.name,
      impactedEmails: item.impactedEmails,
      date: item.date,
      dateLabel: item.date ? formatShortDate(item.date) : 'N/A',
      dataClasses: item.dataClasses,
    }))
    .sort((left, right) => {
      const impactDelta = (right.impactedEmails || 0) - (left.impactedEmails || 0);
      if (impactDelta !== 0) return impactDelta;
      const leftDate = left.date ? new Date(left.date).getTime() : 0;
      const rightDate = right.date ? new Date(right.date).getTime() : 0;
      return rightDate - leftDate;
    })
    .slice(0, 6);

  const recentBreaches = breachRows
    .filter((item) => item.date)
    .slice(0, 6)
    .map((item) => ({
      name: item.name,
      date: item.date,
      dateLabel: formatShortDate(item.date),
      impactedEmails: item.impactedEmails,
    }));

  const summaryMarkdown = String(
    reportResponse?.leaks?.executive_summary ||
      reportResponse?.executive_summary ||
      buildSummaryFallback({
        totalLeakRecords,
        uniqueEmailsCount,
        uniqueBreachDbsCount,
        mostRecentBreach,
      })
  ).trim();

  const severityCounts = SEVERITY_ORDER.reduce((accumulator, label) => ({
    ...accumulator,
    [label]: 0,
  }), {});

  findings.forEach((finding) => {
    severityCounts[finding.severity] = (severityCounts[finding.severity] || 0) + 1;
  });

  const severityStats = SEVERITY_ORDER.map((label) => ({
    label,
    value: severityCounts[label] || 0,
    color: SEVERITY_COLORS[label],
  }));

  const resolvedClientName =
    clientName ||
    reportResponse?.client_name ||
    reportResponse?.leaks?.client_name ||
    reportResponse?.meta?.client_name ||
    reportResponse?.company ||
    leaksPayload?.client_name ||
    'Client Organization';

  const tocItems = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-security-assessment', title: 'Executive Security Assessment', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    { id: 'database-breaches', title: 'Database Breaches', level: 1 },
    { id: 'findings', title: 'Findings', level: 1 },
    ...findings.map((finding) => ({
      id: finding.id,
      title: finding.title,
      level: 2,
    })),
  ];

  return {
    pentestId,
    companyName: companyName || 'StealthNet AI',
    clientName: resolvedClientName,
    organizationName: resolvedClientName,
    reportDate: formatDate(reportResponse?.generated_at || reportResponse?.meta?.generated_at || Date.now()),
    testingType: 'Dark Web Monitoring',
    brand,
    totalFindings: findings.length,
    totalLeakRecords,
    uniqueEmailsCount,
    uniqueBreachDbsCount,
    uniqueLeakSourceCount,
    mostRecentBreach,
    mostRecentBreachLabel: mostRecentBreach?.date ? formatShortDate(mostRecentBreach.date) : 'N/A',
    topImpactedEmails,
    topBreachDatabases,
    recentBreaches,
    executiveSummaryMarkdown: summaryMarkdown,
    executiveSummaryParagraphs: splitSummaryParagraphs(summaryMarkdown),
    executiveSummaryBlocks: markdownToBlocks(summaryMarkdown),
    methodology: DARKWEB_METHODOLOGY_PARAGRAPHS,
    findings,
    breachRows,
    severityStats,
    tocItems,
  };
};
